/**
// @ts-check
 * Formats a Date object into a YYYY-MM-DD string.
 * @param {Date} date The date to format.
 * @returns {string} The formatted date string.
 */
const formatDate = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * @param {string} dateString
 * @param {Date} today
 * @returns {string | null}
 */
const parseRelativeDate = (dateString, today) => {
  if (dateString === 'vandaag' || dateString === 'today') {
    return formatDate(today);
  }
  if (dateString === 'gisteren' || dateString === 'yesterday') {
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    return formatDate(yesterday);
  }
  if (dateString === 'morgen' || dateString === 'tomorrow') {
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    return formatDate(tomorrow);
  }
  return null;
};

/**
 * @param {string} dateString
 * @param {Date} today
 * @returns {string | null}
 */
const parseDayName = (dateString, today) => {
  /** @type {Object<string, number>} */
  const dayAliases = {
    zondag: 0,
    zo: 0,
    zon: 0,
    sunday: 0,
    sun: 0,
    maandag: 1,
    ma: 1,
    maa: 1,
    monday: 1,
    mon: 1,
    dinsdag: 2,
    di: 2,
    din: 2,
    tuesday: 2,
    tue: 2,
    woensdag: 3,
    wo: 3,
    woe: 3,
    wednesday: 3,
    wed: 3,
    donderdag: 4,
    do: 4,
    don: 4,
    thursday: 4,
    thu: 4,
    vrijdag: 5,
    vr: 5,
    vri: 5,
    vrij: 5,
    friday: 5,
    fri: 5,
    zaterdag: 6,
    za: 6,
    zat: 6,
    saturday: 6,
    sat: 6,
  };

  const dayIndex = dayAliases[dateString];
  if (dayIndex === undefined) return null;

  const targetDay = new Date(today);
  const currentDay = targetDay.getDay();
  const daysToAdd = (dayIndex - currentDay + 7) % 7;
  targetDay.setDate(targetDay.getDate() + (daysToAdd === 0 ? 7 : daysToAdd));
  return formatDate(targetDay);
};

/**
 * @param {string} dateString
 * @returns {string | null}
 */
const parseExplicitDate = (dateString) => {
  const dateRegex = /^(\d{1,2})[-/](\d{1,2})[-/](\d{4})$/;
  const match = dateString.match(dateRegex);

  if (!match) return null;

  const day = parseInt(match[1], 10);
  const month = parseInt(match[2], 10) - 1;
  const year = parseInt(match[3], 10);

  const parsedDate = new Date(year, month, day);
  if (
    parsedDate.getFullYear() === year &&
    parsedDate.getMonth() === month &&
    parsedDate.getDate() === day
  ) {
    return formatDate(parsedDate);
  }
  return null;
};

export const parseDateString = (dateString) => {
  if (!dateString) return null;

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const lowerCaseDateString = dateString.trim().toLowerCase();

  const relativeResult = parseRelativeDate(lowerCaseDateString, today);
  if (relativeResult) return relativeResult;

  const dayNameResult = parseDayName(lowerCaseDateString, today);
  if (dayNameResult) return dayNameResult;

  return parseExplicitDate(lowerCaseDateString);
};
